import { COMPARISON, DETAIL_MAX_DAYS } from '../GscTools/period';
import { emptyDraft, resolveScope } from '../GscTools/scope';
import { SCOPE_KEYS, hasScope, scopeDraftFromParams, scopeParamsFromScope } from '../GscTools/url';
import { REVIEW } from './rule';

/**
 * Surfaces Search Console reports no query dimension for. A query shortlist cannot be built on
 * them at all, so they are refused in the scope bar rather than sent and answered with an error.
 */
const NO_QUERY_TYPES = new Set(['discover', 'googleNews']);

/**
 * The scope this report runs under: the shared one, read inside one window.
 *
 * No comparison is offered. Whether several URLs were shown for a query is a fact about one window,
 * and a baseline would be a control that changes nothing — or, worse, would read as a claim that
 * the overlap is new. The window is the shorter detail bound, because every request here carries a
 * query or a page dimension.
 *
 * The candidate rule is not part of the scope on purpose: it never reaches Search Console, and
 * putting it in the resolved scope would imply the rows came back already filtered by it.
 */
export function initialDraft(period) {
    return {
        ...emptyDraft(period),
        // Fixed rather than hidden-but-settable: resolveScope reads this, and "none" is what makes
        // it stop resolving a baseline nothing on the page would use.
        comparison: COMPARISON.NONE,
    };
}

export function resolveReportScope(draft, t) {
    const scope = resolveScope({ ...draft, comparison: COMPARISON.NONE }, t, {
        maxDays: DETAIL_MAX_DAYS,
    });

    if (NO_QUERY_TYPES.has(scope.searchType)) {
        scope.errors.push(t('tools.cannibalization.errors.no_queries'));
    }

    return scope;
}

/** The query-string keys this report owns on top of the shared scope ones. */
const REPORT_KEYS = ['min-impressions', 'min-share', 'min-urls', 'review'];

export const URL_KEYS = [...SCOPE_KEYS, ...REPORT_KEYS];

/** Whether the address describes a report to run, counting this report's own rule too. */
export function hasReportScope(params) {
    return hasScope(params, REPORT_KEYS);
}

/**
 * The draft this report starts from when its address already describes one. The selection is not
 * carried in the address: evaluating is an explicit act that spends a request per query, and a
 * shared link that fired twenty of them on arrival would be spending someone else's budget.
 */
export function reportDraftFromParams(params) {
    return scopeDraftFromParams(params);
}

export function reviewFromParams(params) {
    return Object.values(REVIEW).includes(params.review) ? params.review : REVIEW.ALL;
}

/** The applied scope, the rule and the review view as query parameters. */
export function reportParamsFromScope(scope, rule, review) {
    return {
        ...scopeParamsFromScope(scope),
        'min-impressions': rule.minImpressions,
        'min-share': rule.minShare,
        'min-urls': rule.minUrls,
        review,
    };
}
